import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { HIP_X, HIP_Y, villagerBody, villagerLeg } from '../models/villagers.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { OmenCtx } from '../omens/common.ts';

/**
 * The rescue in the woods (Caras prestadas 3, docs/SAGAS.md): the woodcutter and his daughter down in the pit,
 * reaching up, until a hero stands by it long enough (a ring fills); then each one walks after the hero who pulled
 * them out, a small health bar over them, toward a warm lantern at the node that says «bring them here» (a breathing
 * ring of the radius that counts as home). Freed: earth and roots burst; home: a warm flare; fallen: a grey fade.
 *
 * Draw calls: two instanced bodies (man, child), their legs, the ground rings (one shader), one glow layer. No lights.
 */

const K = WORLD_PER_HEX / 1000;
/** The order they come out: the woodcutter first, then his daughter (villager kinds: 0 hooded man, 3 child). */
const KINDS = [0, 3];
const BIG = 1.25;
const WARM = new THREE.Color('#ffcf9e');
const HOT = new THREE.Color('#fff1dc');
const COLD = new THREE.Color('#9fb0d8');
const RED = new THREE.Color('#ff4f3a');

const RING_VERT = /* glsl */ `
attribute vec4 iRing; // x, y, z, radius
attribute vec4 iTint; // r, g, b, fill (−1: a plain breathing ring)
varying vec2 vUv;
varying vec4 vTint;
void main() {
  vUv = uv;
  vTint = iTint;
  vec3 p = iRing.xyz + vec3(position.x, 0.0, -position.y) * iRing.w * 2.0;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}`;

const RING_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
varying vec4 vTint;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float a;
  vec3 c = vTint.rgb;
  if (vTint.a < -1.5) {
    // The pit while someone is in it: a slow dragon-red pulse round it, the objective from afar.
    float pulse = 0.5 + 0.5 * sin(uTime * 2.4);
    float rim = smoothstep(0.8, 0.9, r) * (1.0 - smoothstep(0.94, 1.0, r));
    float wave = smoothstep(0.0, 0.06, r - pulse * 0.85) * (1.0 - smoothstep(0.06, 0.16, r - pulse * 0.85)) * (1.0 - pulse);
    a = rim * (0.45 + 0.35 * pulse) + wave * 0.35 * step(r, 0.92);
  } else if (vTint.a < 0.0) {
    // Home: a soft warm floor and a breathing rim.
    float rim = smoothstep(0.86, 0.93, r) * (1.0 - smoothstep(0.96, 1.0, r));
    float floor_ = (1.0 - smoothstep(0.0, 0.95, r)) * 0.07;
    a = (rim * (0.3 + 0.12 * sin(uTime * 2.2)) + floor_);
  } else {
    // The pit: a band that fills round as the hero holds on.
    float band = smoothstep(0.72, 0.76, r) * (1.0 - smoothstep(0.93, 0.97, r));
    float ang = fract(atan(p.x, p.y) / 6.2831853 + 0.5);
    float filled = step(ang, vTint.a);
    c = mix(vec3(0.8, 0.84, 0.95), vec3(1.0, 0.54, 0.43), filled);
    a = band * mix(0.4, 1.0, filled);
  }
  if (a < 0.01) discard;
  gl_FragColor = vec4(c, a);
  #include <colorspace_fragment>
}`;

interface Track {
  px: number;
  py: number;
  cx: number;
  cy: number;
  dist: number;
}

export class RescueLayer {
  readonly group = new THREE.Group();
  private bodies: THREE.InstancedMesh[];
  private legs: THREE.InstancedMesh;
  private rings: THREE.InstancedBufferGeometry;
  private ringPos = new Float32Array(4 * 4);
  private ringTint = new Float32Array(4 * 4);
  private ringMat: THREE.ShaderMaterial;
  private glow: GlowLayer;
  private mat: THREE.MeshStandardMaterial;
  private track = new Map<number, Track>();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e = new THREE.Euler();
  private now = 0;

  constructor(
    private ctx: OmenCtx,
    private pit: { x: number; y: number },
    private home: number,
    private holdTicks: number,
  ) {
    this.group.name = 'rescue';
    this.mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true, emissive: '#3a2a1e', emissiveIntensity: 1 });
    this.bodies = KINDS.map((k) => {
      const im = new THREE.InstancedMesh(villagerBody(k, k * 3 + 1), this.mat, 2);
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      im.count = 0;
      im.frustumCulled = false;
      this.group.add(im);
      return im;
    });
    this.legs = new THREE.InstancedMesh(villagerLeg(), this.mat, 8);
    this.legs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.legs.count = 0;
    this.legs.frustumCulled = false;
    this.group.add(this.legs);
    this.rings = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1);
    this.rings.index = quad.index;
    this.rings.setAttribute('position', quad.getAttribute('position'));
    this.rings.setAttribute('uv', quad.getAttribute('uv'));
    this.rings.setAttribute('iRing', new THREE.InstancedBufferAttribute(this.ringPos, 4).setUsage(THREE.DynamicDrawUsage));
    this.rings.setAttribute('iTint', new THREE.InstancedBufferAttribute(this.ringTint, 4).setUsage(THREE.DynamicDrawUsage));
    this.rings.instanceCount = 0;
    this.ringMat = new THREE.ShaderMaterial({ vertexShader: RING_VERT, fragmentShader: RING_FRAG, uniforms: { uTime: { value: 0 } }, transparent: true, depthWrite: false });
    const rm = new THREE.Mesh(this.rings, this.ringMat);
    rm.frustumCulled = false;
    rm.renderOrder = 6;
    this.group.add(rm);
    this.glow = new GlowLayer(ctx.low ? 24 : 48);
    this.group.add(this.glow.mesh);
  }

  /** Every fresh sim tick: where each freed one was and is (for the stride between ticks). */
  tick(s: BattleState): void {
    const seen = new Set<number>();
    for (const p of s.rescue?.people ?? []) {
      seen.add(p.id);
      const t = this.track.get(p.id);
      if (!t) this.track.set(p.id, { px: p.x, py: p.y, cx: p.x, cy: p.y, dist: 0 });
      else {
        t.dist += Math.hypot(p.x - t.cx, p.y - t.cy) * K;
        t.px = t.cx;
        t.py = t.cy;
        t.cx = p.x;
        t.cy = p.y;
      }
    }
    for (const id of [...this.track.keys()]) if (!seen.has(id)) this.track.delete(id);
  }

  /** The rescue's moments: pulled out (earth and roots fly), home (a warm flare), fallen (a grey fade). */
  event(kind: 'free' | 'safe' | 'down', x: number, y: number): void {
    const p = this.ctx.at(x, y, 0.1);
    if (kind === 'free') {
      this.ctx.vfx.burst(p, { n: this.ctx.low ? 10 : 22, color: '#4a3a2c', speed: 0.05, up: 0.07, size: 0.08, life: 22, gravity: 0.006, spread: 0.4 });
      this.ctx.vfx.burst(p, { n: this.ctx.low ? 4 : 8, color: '#6b5238', speed: 0.035, up: 0.05, size: 0.13, life: 26, gravity: 0.005, spread: 0.3 });
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.2, 1.3, '#c9b8a0', 12, 0.6);
      this.ctx.shake(0.1);
    } else if (kind === 'safe') {
      this.ctx.flare(this.ctx.at(x, y, 0.8), '#ffd9b0', 3.5, 18);
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.3, 1.8, '#ffcf9e', 14, 0.7);
      this.ctx.vfx.burst(this.ctx.at(x, y, 0.5), { n: this.ctx.low ? 8 : 16, color: '#ffd9b0', speed: 0.02, up: 0.05, size: 0.06, life: 26, gravity: -0.002, spread: 0.4 });
    } else {
      this.ctx.vfx.burst(this.ctx.at(x, y, 0.4), { n: this.ctx.low ? 6 : 12, color: '#5a5e68', speed: 0.015, up: 0.02, size: 0.4, life: 40, smoke: true, spread: 0.2 });
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.2, 1.2, '#7d808a', 16, 0.5);
    }
  }

  /** Every redraw: the pit and who is still in it, the ones walking home and their bars, the home ring. */
  draw(s: BattleState, alpha: number, contact: (x: number, y: number, z: number, size: number) => void, bar: (p: THREE.Vector3, frac: number) => void): void {
    const r = s.rules?.rescue;
    const st = s.rescue;
    if (!r) return;
    const left = st?.left ?? r.people;
    const counts = [0, 0];
    let legs = 0;
    let rings = 0;
    const ring = (x: number, y: number, z: number, rad: number, c: THREE.Color, fill: number): void => {
      this.ringPos.set([x, y, z, rad], rings * 4);
      this.ringTint.set([c.r, c.g, c.b, fill], rings * 4);
      rings++;
    };
    const person = (slot: number, ground: THREE.Vector3, yaw: number, stride: number, sink: number, lean: number): void => {
      const kind = KINDS[slot] as number;
      const kid = kind === 3;
      const sc = SCALE.hero * BIG * (kid ? 0.68 : 1);
      const bob = Math.abs(Math.sin(stride)) * 0.03;
      const body = this.bodies[slot] as THREE.InstancedMesh;
      const i = counts[slot] as number;
      if (i >= 2) return;
      this.e.set(lean, yaw + Math.sin(stride) * 0.04, 0, 'YXZ');
      this.q.setFromEuler(this.e);
      this.m.compose(this.v.set(ground.x, ground.y + bob - sink, ground.z), this.q, this.s3.set(sc, sc, sc));
      body.setMatrixAt(i, this.m);
      counts[slot] = i + 1;
      for (const sd of [-1, 1]) {
        if (legs >= 8) break;
        this.e.set(Math.sin(stride) * 0.55 * sd, yaw, 0, 'YXZ');
        this.q.setFromEuler(this.e);
        const hx = sd * HIP_X * sc;
        this.m.compose(this.v.set(ground.x + Math.cos(yaw) * hx, ground.y + HIP_Y * sc + bob - sink, ground.z - Math.sin(yaw) * hx), this.q, this.s3.set(sc, sc, sc));
        this.legs.setMatrixAt(legs++, this.m);
      }
      if (sink === 0) contact(ground.x, ground.y, ground.z, kid ? 0.42 : 0.55);
    };
    this.glow.begin();
    // In the pit: whoever is still down there, sunk to the chest, leaning back to look up.
    const pitG = this.ctx.at(this.pit.x, this.pit.y, 0);
    const freed = r.people - left;
    for (let k = freed; k < r.people && k < KINDS.length; k++) {
      const off = (k - freed - (left - 1) / 2) * 0.32;
      const g = pitG.clone();
      g.x += off;
      g.z += 0.05;
      const yaw = Math.atan2(-g.x, -g.z) + Math.sin(this.now * 0.8 + k) * 0.25;
      person(k, g, yaw, this.now * 0.6 + k, KINDS[k] === 3 ? 0.28 : 0.42, -0.25);
    }
    if (left > 0) {
      // A faint cold light from above on the ones down there (the hole stays dark), the red pulse that marks the
      // objective from afar, and the ring filling while a hero holds on there.
      for (let k = freed; k < r.people && k < KINDS.length; k++) {
        const off = (k - freed - (left - 1) / 2) * 0.32;
        this.glow.add(pitG.x + off, pitG.y + 0.3, pitG.z + 0.05, COLD, 0.6, 0.3 + 0.06 * Math.sin(this.now * 1.7 + k));
      }
      // The band's outer edge is where standing counts (`free`), out past the mound of clods round the lip that hid it
      // when it sat inside; the red pulse just beyond it.
      const stand = (r.free * K) / 0.97;
      ring(pitG.x, pitG.y + 0.06, pitG.z, stand * 1.22, RED, -2);
      ring(pitG.x, pitG.y + 0.07, pitG.z, stand, WARM, Math.min(1, (st?.held ?? 0) / this.holdTicks));
    }
    // The ones following their hero home.
    for (const p of st?.people ?? []) {
      const t = this.track.get(p.id);
      const sx = t ? t.px + (t.cx - t.px) * alpha : p.x;
      const sy = t ? t.py + (t.cy - t.py) * alpha : p.y;
      const slot = Math.min(KINDS.length - 1, p.id - 1);
      // Two after the same hero walk side by side, not inside each other.
      const g = this.ctx.at(sx + (slot ? 420 : -420), sy + (slot ? -260 : 260), 0);
      person(slot, g, facingToRotY(p.facing), p.moving ? (t?.dist ?? 0) * 5.2 : 0, 0, 0);
      const kid = KINDS[slot] === 3;
      bar(this.v.set(g.x, g.y + (kid ? 1.05 : 1.45) * SCALE.hero * BIG + 0.12, g.z).clone(), Math.max(0, p.hp / p.maxHp));
    }
    // Home: a lantern's warm floor round the node while anyone is still out there.
    if (left > 0 || (st?.people.length ?? 0) > 0) {
      const n = this.ctx.at(0, 0, 0);
      ring(n.x, n.y + 0.04, n.z, this.home * K, WARM, -1);
      this.glow.add(n.x, n.y + 2.4, n.z, WARM, 0.9, 0.35 + 0.1 * Math.sin(this.now * 2.2));
      this.glow.add(n.x, n.y + 1.2, n.z, HOT, 2.6, 0.08);
    }
    this.glow.end();
    for (const b of this.bodies) b.instanceMatrix.needsUpdate = true;
    this.bodies.forEach((b, i) => (b.count = counts[i] as number));
    this.legs.count = legs;
    this.legs.instanceMatrix.needsUpdate = true;
    this.rings.instanceCount = rings;
    (this.rings.getAttribute('iRing') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (this.rings.getAttribute('iTint') as THREE.InstancedBufferAttribute).needsUpdate = true;
  }

  frame(now: number): void {
    this.now = now;
    (this.ringMat.uniforms.uTime as { value: number }).value = now;
  }

  dispose(): void {
    for (const b of this.bodies) {
      b.geometry.dispose();
      b.dispose();
    }
    this.legs.geometry.dispose();
    this.legs.dispose();
    this.mat.dispose();
    this.rings.dispose();
    this.ringMat.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
